import React, { useEffect, useState } from 'react';
import SideSlidingModal from '../../../components/modal/wrapper/side-sliding-modal';
import InputField from '../../../components/form/input-field';
import SelectField from '../../../components/form/select-field';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import ApiRequest from '../../../libs/ApiCall';
import { CurrencyTypes, DurationTypes, EmptyFormInput, removeCommas, SmoothScroll } from "../../../libs/helper-support";
import { Check } from 'lucide-react';
import { ErrorFlash, SuccessFlash } from '../../../libs/flash-notification';
import debounce from 'lodash.debounce';
import DatePickerField from '../../../components/form/date-picker';

type AddButtonType = {
    buttonDesign: JSX.Element,
    onSuccess?: (data:any) => void,
    onLoading?: (state:boolean) => void
}
const AddProduct = ({buttonDesign, onSuccess, onLoading} : AddButtonType) => {
 
  interface SuggestionType{
    name_institution: string,
  }

  const [issuingHouses, setIssuingHouses] = useState<string[]>([]);
  const [issuingHouseInput, setIssuingHouse] = useState<string>('');
  const [onEditIssuingHouse, setOnEditIssuingHouse] = useState<null|number>(null);
  
  const [suggestions, setSuggestions] = useState<SuggestionType[]>([]);
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);

  type OptionType = {
    value: string,
    name: string
  }

  const [productTypes,  setProductTypes] = useState<OptionType[]>([]);

  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const handleKeyDown = (event:React.KeyboardEvent<HTMLInputElement>) => {
    
    event.key === 'Enter' && event.preventDefault();
    
    if ( onEditIssuingHouse !== null && event.key === 'Enter' ) {
      updateIssuingHouse(onEditIssuingHouse?? 0);  
      return;
    }
    event.key === 'Enter' && addNewIssuingHouse();
  };


  const addNewIssuingHouse = (suggestion: any = null) => {
    suggestion = suggestion ?? issuingHouseInput;
    if ( suggestion === '' ) return;
    setIssuingHouses([...issuingHouses, suggestion]);
    setIssuingHouse('');
    setShowSuggestions(false);
  }

  const updateIssuingHouse = (index:number, suggestion: any = null) => {
    suggestion = suggestion ?? issuingHouseInput;
    if ( suggestion === '' ) return;
    issuingHouses[index] = suggestion;
    setIssuingHouses([...issuingHouses]);
    setIssuingHouse('');
    setOnEditIssuingHouse(null);
    setShowSuggestions(false);
  }

  const onsetForceModalClose = (state:any) => {setModalOpen(state)}

  const formSchema = yup.object({
    issuer: yup.string().required(),
    amount: yup.string().required(),
    interestRate: yup.string().required('Interest rate is required'),
    tenor: yup.string().required('Tenor is a required field'),
    productTypeId: yup.string().required('Product type is required'),
    // offerOpenDate: yup.string().required('Offer open date is required'),
    // offerCloseDate: yup.string().required('Offer close date is required'),
    offerOpenDate: yup
      .string()
      .required('Offer open date is required')
      .test(
        'start-before-end',
        'Open date cannot be later than Close date',
        function (value) {
          const { offerCloseDate } = this.parent;

          if (!value || !offerCloseDate) return true;

          return new Date(value) <= new Date(offerCloseDate);
        }
      ),

    offerCloseDate: yup
      .string()
      .required('Offer close date is required')
      .test(
        'end-after-start',
        'Close date cannot be earlier than Open date',
        function (value) {
          const { offerOpenDate } = this.parent;

          if (!value || !offerOpenDate) return true;

          return new Date(value) >= new Date(offerOpenDate);
        }
      ),
    issuingHouses: yup.array().of(yup.string()),
    series: yup.string().required('Series is a required field'),
    amountCurrency: yup.string().required('This field is required'),
    tenorIn: yup.string().required('Tenor duration is required'),
    offerPrice: yup.string().required('Offer price is required'),
    offerPriceCurrency: yup.string().required('This field is required'),
    rightRatio: yup.string().required('Right ratio is required'),
});

const  {
    setValue,
    setError,
    formState: {errors},
    handleSubmit,
    getValues,
    reset,
} = useForm({resolver: yupResolver(formSchema)}); 

const AddProduct = async (data:any) => {
  
  data['issuingHouses'] = issuingHouses;
  data['amount'] = removeCommas(data['amount']);
  data['offerPrice'] = removeCommas(data['offerPrice']);
  onLoading && onLoading(true);
  const request = await ApiRequest({endpoint: '/product/add-new', method:'POST', payload: data});
  onLoading && onLoading(false);
  
  if ( request.status === '200') {
    onSuccess && onSuccess(request.data);
    setModalOpen(false);
    reset(EmptyFormInput(formSchema));
    setIssuingHouses([]);
    SuccessFlash("Product added successfully");
    return;
  }

  ErrorFlash(request.message);

  if (request.status === "payloadValidationError") {
    for (const [field, messages] of Object.entries(request.data)) {
        const input_field : any = field ;
        const error_message : any = messages
        setError(input_field, { type: "manual", message: error_message.join(", ") });
        SmoothScroll();
    }
  }
}

const getProductTypes = async () => {
  const request = await ApiRequest({endpoint: '/product-types', method: 'GET'});
  
  if(request.status === '200') {
    setProductTypes(request.data.map((item: any) => ({value: item.uuid, name: item.name})))
    
  }
}

useEffect(() => {
  getProductTypes()
  setValue('amountCurrency', 'NGN');
  setValue('offerPriceCurrency', 'NGN');
  setValue('tenorIn', 'months');
}, []);

const fetchSuggestions = debounce(async (searchQuery) => {
  const request = await ApiRequest({endpoint: `/search/member?query=${searchQuery}`, method: 'GET'});
  setShowSuggestions(false);
  setSuggestions([]);
  if(request.status === '200'){  
    setSuggestions(request.data);
    setShowSuggestions(true);
  }
}, 300);

useEffect(() => {
  if (issuingHouseInput && ! issuingHouses.find((item) => item === issuingHouseInput)) fetchSuggestions(issuingHouseInput);
  return fetchSuggestions.cancel;
}, [issuingHouseInput]);
  
    return (
        <SideSlidingModal buttonTrigger={buttonDesign} forceModalClose={modalOpen} onSetForceModalClose={onsetForceModalClose}>
          <div>
            <h2 className="page-title text-[20px]">Add New Product</h2>
            <p className="mt-4 text-light-black text-[14px] font-[450]">Enter relevant details and ignore fields that do not apply</p>
            <form onSubmit={handleSubmit(AddProduct)} className='grid mt-10 gap-y-5'>
              <div>
                <InputField
                  placeholder='Enter company name'
                  label='Issuer'
                  error={errors.issuer?.message}
                  onChangeInput={(e) => [setValue('issuer', e.target.value), setError('issuer', {message: ''})]}
                  value={getValues('issuer')}
                />
              </div>
              <div>
                <SelectField
                  label='Product Type'
                  placeholder='Select'
                  error={errors.productTypeId?.message}
                  onChangeInput={(e) => [setValue('productTypeId', e), setError('productTypeId', {message: ''})]}
                  value={getValues('productTypeId')}
                  options={productTypes}
                />
              </div>
              <div className='flex w-full space-x-3'>
                  <div className='w-4/5'>
                    <InputField
                      placeholder='Enter figure'
                      type='money-format'
                      label='Amount'
                      error={errors.amount?.message}
                      onChangeInput={(e) => [setValue('amount', e.target.value), setError('amount', {message: ''})]}
                      value={getValues('amount')}
                    />
                  </div>
                  <div className='w-1/5'>
                    <SelectField
                      label='currency'
                      labelClass="opacity-0"
                      placeholder='Select'
                      options={CurrencyTypes}
                      error={errors.amountCurrency?.message}
                      onChangeInput={(e) => [setValue('amountCurrency', e), setError('amountCurrency', {message: ''})]}
                      value={getValues('amountCurrency') ?? 'NGN'}
                    />
                  </div>
              </div>
              <div className='flex w-full space-x-3'>
                  <div className='w-4/5'>
                    <InputField
                      placeholder='Enter figure'
                      type='money-format'
                      label='Offer Price'
                      error={errors.offerPrice?.message}
                      onChangeInput={(e) => [setValue('offerPrice', e.target.value), setError('offerPrice', {message: ''})]}
                      value={getValues('offerPrice')}
                    />
                  </div>
                  <div className='w-1/5'>
                    <SelectField
                      placeholder='Select'
                      label='currency'
                      labelClass="opacity-0"
                      error={errors.offerPriceCurrency?.message}
                      onChangeInput={(e) => [setValue('offerPriceCurrency', e), setError('offerPriceCurrency', {message: ''})]}
                      value={getValues('offerPriceCurrency') ?? 'NGN'}
                      options={CurrencyTypes}
                    />
                  </div>
              </div>
              <div>
                <InputField
                  placeholder='Enter figure'
                  label='Interest rate or Yield'
                  type='number'
                  error={errors.interestRate?.message}
                  onChangeInput={(e) => [setValue('interestRate', e.target.value), setError('interestRate', {message: ''})]}
                  value={getValues('interestRate')}
                  icon={<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12.8537 3.85261L3.85365 12.8526C3.75983 12.9464 3.63258 12.9991 3.4999 12.9991C3.36722 12.9991 3.23997 12.9464 3.14615 12.8526C3.05233 12.7588 2.99962 12.6315 2.99962 12.4989C2.99962 12.3662 3.05233 12.2389 3.14615 12.1451L12.1462 3.14511C12.2399 3.05129 12.3671 2.99855 12.4997 2.99849C12.6323 2.99843 12.7595 3.05106 12.8533 3.14479C12.9472 3.23853 12.9999 3.3657 13 3.49832C13 3.63095 12.9474 3.75816 12.8537 3.85198V3.85261ZM3.15865 6.34011C2.73671 5.91808 2.4997 5.34573 2.49976 4.74895C2.49981 4.15217 2.73694 3.57986 3.15896 3.15792C3.58099 2.73598 4.15335 2.49896 4.75012 2.49902C5.3469 2.49908 5.91921 2.73621 6.34115 3.15823C6.76309 3.58026 7.00011 4.15261 7.00005 4.74939C6.99999 5.34617 6.76287 5.91848 6.34084 6.34042C5.91881 6.76236 5.34646 6.99937 4.74968 6.99932C4.15291 6.99926 3.5806 6.76213 3.15865 6.34011ZM3.4999 4.75011C3.50006 4.95564 3.5509 5.15796 3.64792 5.33915C3.74493 5.52034 3.88513 5.67482 4.05609 5.7889C4.22706 5.90298 4.42351 5.97314 4.62806 5.99317C4.83261 6.01321 5.03895 5.98249 5.2288 5.90375C5.41865 5.82501 5.58615 5.70067 5.71647 5.54174C5.8468 5.38281 5.93592 5.1942 5.97596 4.99261C6.01599 4.79101 6.0057 4.58266 5.94599 4.38599C5.88629 4.18933 5.77901 4.01042 5.63365 3.86511C5.45876 3.69027 5.23594 3.57124 4.99339 3.52307C4.75083 3.4749 4.49943 3.49976 4.27101 3.5945C4.04258 3.68924 3.84739 3.84961 3.71013 4.05531C3.57287 4.26102 3.49971 4.50281 3.4999 4.75011ZM13.4999 11.2501C13.4998 11.7707 13.3192 12.275 12.9889 12.6774C12.6585 13.0797 12.1989 13.355 11.6884 13.4564C11.1778 13.5579 10.6479 13.4792 10.1889 13.2337C9.72982 12.9882 9.37011 12.5912 9.171 12.1102C8.9719 11.6293 8.94571 11.0941 9.09691 10.596C9.24811 10.0979 9.56734 9.66768 10.0002 9.37856C10.4331 9.08944 10.9528 8.95935 11.4708 9.01046C11.9889 9.06157 12.4731 9.29072 12.8412 9.65886C13.0507 9.86735 13.2169 10.1153 13.33 10.3885C13.443 10.6616 13.5008 10.9545 13.4999 11.2501ZM12.4999 11.2501C12.5 10.9609 12.3997 10.6806 12.2163 10.457C12.0329 10.2335 11.7776 10.0804 11.494 10.0239C11.2104 9.96743 10.9159 10.011 10.6609 10.1473C10.4058 10.2836 10.2059 10.5041 10.0951 10.7713C9.98442 11.0384 9.96976 11.3357 10.0537 11.6125C10.1376 11.8892 10.3148 12.1284 10.5553 12.2891C10.7957 12.4498 11.0844 12.5221 11.3722 12.4938C11.66 12.4655 11.9291 12.3383 12.1337 12.1339C12.2501 12.0181 12.3424 11.8804 12.4053 11.7287C12.4682 11.577 12.5003 11.4143 12.4999 11.2501Z" fill="#888888"/>
                    </svg>
                    }
                />
              </div>
              <div>
                <InputField
                  label='Right ratio'
                  placeholder='Enter figure'
                  type='number'
                  error={errors.rightRatio?.message}
                  onChangeInput={(e) => [setValue('rightRatio', e.target.value), setError('rightRatio', {message: ''})]}
                  value={getValues('rightRatio')}
                />
              </div>
              <div className='flex w-full space-x-3'>
                  <div className='w-4/5'>
                    <InputField
                      placeholder='Enter figure'
                      type='number'
                      label='Tenor'
                      error={errors.tenor?.message}
                      onChangeInput={(e) => [setValue('tenor', e.target.value), setError('tenor', {message: ''})]}
                      value={getValues('tenor')}
                    />
                  </div>
                  <div className='w-1/5'>
                    <SelectField
                      placeholder="Select"
                      label='month'
                      labelClass="opacity-0"
                      error={errors.tenorIn?.message}
                      onChangeInput={(e) => [setValue('tenorIn', e), setError('tenorIn', {message: ''})]}
                      value={getValues('tenorIn') ?? 'months'}
                      options={DurationTypes}
                    />
                  </div>
              </div>
              <div>
                <InputField
                  label='Series'
                  placeholder='Enter series'
                      error={errors.series?.message}
                      onChangeInput={(e) => [setValue('series', e.target.value), setError('series', {message: ''})]}
                      value={getValues('series')}
                />
              </div>
            {/* <div>
                <DatePickerField
                  label='Offer Open Date'
                      error={errors.offerOpenDate?.message}
                      onChangeInput={(value) => [setValue('offerOpenDate', value), setError('offerOpenDate', {message: ''})]}
                      value={getValues('offerOpenDate')}
                      placeholder='Choose date'
                />
              </div>
              <div>
                <DatePickerField
                    label='Offer Close Date'
                    error={errors.offerCloseDate?.message}
                      onChangeInput={(e) => [setValue('offerCloseDate', e), setError('offerCloseDate', {message: ''})]}
                      value={getValues('offerCloseDate')}
                      placeholder='Choose date'
                />
              </div> */}

            <div>
              <DatePickerField
                label='Offer Open Date'
                error={errors.offerOpenDate?.message}
                value={getValues('offerOpenDate')}
                placeholder='Choose date'
                onChangeInput={(value) => {
                  const endDate = getValues('offerCloseDate');

                  setValue('offerOpenDate', value);

                  // Validate start <= end
                  if (endDate && new Date(value) > new Date(endDate)) {
                    setError('offerOpenDate', {
                      message: 'Open date cannot be later than Close date',
                    });
                  } else {
                    setError('offerOpenDate', { message: '' });
                    setError('offerCloseDate', { message: '' });
                  }
                }}
              />
            </div>

            <div>
              <DatePickerField
                label='Offer Close Date'
                error={errors.offerCloseDate?.message}
                value={getValues('offerCloseDate')}
                placeholder='Choose date'
                onChangeInput={(value) => {
                  const startDate = getValues('offerOpenDate');

                  setValue('offerCloseDate', value);

                  // Validate end >= start
                  if (startDate && new Date(value) < new Date(startDate)) {
                    setError('offerCloseDate', {
                      message: 'Close date cannot be earlier than Open date',
                    });
                  } else {
                    setError('offerCloseDate', { message: '' });
                    setError('offerOpenDate', { message: '' });
                  }
                }}
              />
            </div>
              <div>
                <label className='label'>Issuing Houses</label>
                <p className='text-light-black text-[13px] mt-1 font-[450]'>List the issuing houses involved in this product</p>
                <div className='w-full px-5 py-2 min-h-[8em] gap-4 mt-2 border border-[#E7E7E7] font-[450] bg-white rounded-[5px] grid lg:grid-cols-3 items-baseline'>
                  {issuingHouses.map((issuingHouse, index) => (
                    onEditIssuingHouse === index ? (
                      <div className='relative'>
                        <div className='flex relative w-full space-x-3 text-[14px] items-center justify-between'>
                          <input 
                            onKeyDown={handleKeyDown} 
                            value={issuingHouseInput}
                            onInput={(e) => setIssuingHouse(e.currentTarget.value)}
                            className='w-full border-b border-[#E7E7E7] h-fit p-2 cursor-pointer focus:outline-none capitalize' placeholder='Enter issuing house'
                          />
                          {issuingHouse && <button className='absolute right-0 z-10 pr-3 bg-white'  onClick={() => updateIssuingHouse(index)} type='button'><Check size={18}/></button>}
                        </div>
                        {(showSuggestions && suggestions.length > 0) && (
                          <div className='absolute w-full bg-white h-[10em] overflow-y-auto z-10 rounded shadow-sm p-2'>
                          <ul>
                            {suggestions.map((suggestion, i) => (
                              <li key={i} className='capitalize cursor-pointer' onClick={() => updateIssuingHouse(index,suggestion.name_institution)}>{suggestion.name_institution}</li>
                            ))}
                          </ul>
                      </div>
                        )}
                      </div>
                    ) : (
                      <div className='flex relative w-full space-x-3 text-[14px] items-center justify-between'>
                        <div onClick={()=> {setIssuingHouse(issuingHouse); setOnEditIssuingHouse(index)}} className='w-full border relative border-[#E7E7E7] rounded-[7px] h-fit p-2 cursor-pointer overflow-x-hidden'><span className='overflow-x-auto capitalize text-nowrap'>{issuingHouse}</span></div>
                        <button className='absolute right-[0.5px] z-10 pr-3 bg-white' type='button' onClick={() => setIssuingHouses(issuingHouses.filter((_, i) => i !== index))}>
                        <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12.8538 12.646C12.9002 12.6925 12.9371 12.7476 12.9622 12.8083C12.9874 12.869 13.0003 12.9341 13.0003 12.9998C13.0003 13.0655 12.9874 13.1305 12.9622 13.1912C12.9371 13.2519 12.9002 13.3071 12.8538 13.3535C12.8073 13.4 12.7522 13.4368 12.6915 13.462C12.6308 13.4871 12.5657 13.5001 12.5 13.5001C12.4343 13.5001 12.3693 13.4871 12.3086 13.462C12.2479 13.4368 12.1927 13.4 12.1463 13.3535L8.00003 9.20666L3.85378 13.3535C3.75996 13.4474 3.63272 13.5001 3.50003 13.5001C3.36735 13.5001 3.2401 13.4474 3.14628 13.3535C3.05246 13.2597 2.99976 13.1325 2.99976 12.9998C2.99976 12.8671 3.05246 12.7399 3.14628 12.646L7.29316 8.49979L3.14628 4.35354C3.05246 4.25972 2.99976 4.13247 2.99976 3.99979C2.99976 3.86711 3.05246 3.73986 3.14628 3.64604C3.2401 3.55222 3.36735 3.49951 3.50003 3.49951C3.63272 3.49951 3.75996 3.55222 3.85378 3.64604L8.00003 7.79291L12.1463 3.64604C12.2401 3.55222 12.3674 3.49951 12.5 3.49951C12.6327 3.49951 12.76 3.55222 12.8538 3.64604C12.9476 3.73986 13.0003 3.86711 13.0003 3.99979C13.0003 4.13247 12.9476 4.25972 12.8538 4.35354L8.70691 8.49979L12.8538 12.646Z" fill="#888888"/>
                        </svg>
                        </button>
                    </div>
                    )
                  ))}
                  { onEditIssuingHouse === null && <div className='relative'>
                    <div className='flex relative w-full space-x-3 text-[14px] items-center justify-between'>
                    <input 
                      onKeyDown={handleKeyDown} 
                      value={issuingHouseInput}
                      onInput={(e) => setIssuingHouse(e.currentTarget.value)}
                      className='w-full border-b border-[#E7E7E7] h-fit p-2 cursor-pointer focus:outline-none capitalize' placeholder='Enter issuing house'
                    />
                    {issuingHouseInput && <button className='absolute right-0 z-10 pr-3 bg-white' onClick={() => addNewIssuingHouse()} type='button'><Check size={18}/></button>}
                    
                  </div>
                  {(showSuggestions && suggestions.length > 0) && (
                          <div className='absolute w-full bg-white h-[10em] overflow-y-auto z-10 rounded shadow-sm p-2'>
                          <ul>
                            {suggestions.map((suggestion, i) => (
                              <li key={i} className='capitalize cursor-pointer' onClick={() => addNewIssuingHouse(suggestion.name_institution)}>{suggestion.name_institution}</li>
                            ))}
                          </ul>
                      </div>
                        )}
                    </div>}
                </div>
                {errors.issuingHouses && <p className="my-1 text-sm text-red-500 label">{errors.issuingHouses.message}</p>}
              </div>
              <div className='mt-5'>
                  <button className='pryBtn w-fit'>Add Product</button>
              </div>
            </form>
          </div>
        </SideSlidingModal>
    )
}

export default AddProduct